import { Layer } from '../types/design';
import { PageData } from '../pages/template-editor/types';

export type HistoryActionType =
  | 'LAYER_ADD'
  | 'LAYER_MOVE'
  | 'LAYER_DELETE'
  | 'LAYER_EDIT'
  | 'LAYER_STYLE'
  | 'LAYER_REORDER'
  | 'TEXT_STYLE_CHANGE'
  | 'TEMPLATE_APPLY'
  | 'TEMPLATE_LOAD'
  | 'PAGE_ADD'
  | 'DELETE_ELEMENT'
  | 'ADD_ELEMENT'
  | 'DUPLICATE_ELEMENT'
  | 'GROUP_LAYERS'
  | 'ALIGN_ELEMENTS'
  | 'STYLE_CHANGE'
  | 'CANVAS_RESIZE';

export interface HistoryState {
  id?: string;
  dynamicLayers: Layer[];
  layerPositions: Record<string, { left: number; top: number }>;
  textContents: Record<string, string>;
  textStyles: Record<string, any>;
  shapeSettings: Record<string, any>;
  backgroundSettings?: Record<string, any>;
  imageSettings?: Record<string, any>;
  videoSettings?: Record<string, any>;
  scrollerSettings?: Record<string, any>;
  widgetSettings?: Record<string, any>;
  templateSettings?: any;
  canvasDimensions?: { width: number; height: number; unit?: string };
  pages?: PageData[];
  currentPageIndex?: number;
  pageName?: string;
  timestamp: number;
  actionType?: HistoryActionType;
  actionDescription?: string;
}

export interface HistoryManager {
  past: HistoryState[];
  present: HistoryState | null;
  future: HistoryState[];
}

export const createEmptyHistory = (): HistoryManager => ({
  past: [],
  present: null,
  future: []
});

export const canUndo = (history: HistoryManager): boolean => {
  return history.past.length > 0;
};

export const canRedo = (history: HistoryManager): boolean => {
  return history.future.length > 0;
};

export const performUndo = (history: HistoryManager): HistoryManager => {
  if (!canUndo(history)) return history;

  const { past, present, future } = history;
  const previous = past[past.length - 1];
  const newPast = past.slice(0, past.length - 1);

  return {
    past: newPast,
    present: previous,
    future: present ? [present, ...future] : future
  };
};

export const performRedo = (history: HistoryManager): HistoryManager => {
  if (!canRedo(history)) return history;

  const { past, present, future } = history;
  const next = future[0];
  const newFuture = future.slice(1);

  return {
    past: present ? [...past, present] : past,
    present: next,
    future: newFuture
  };
};

export const commitAction = (
  history: HistoryManager,
  newState: HistoryState
): HistoryManager => {
  const { past, present } = history;

  // Don't commit if state hasn't changed
  if (present && isStateEqual(present, newState)) {
    console.log('🔙 [HistoryManager] Commit rejected: State is identical to present.');
    return history;
  }

  console.log(`✅ [HistoryManager] Committing new state (Action: ${newState.actionType || 'Unknown'}, Description: ${newState.actionDescription || 'N/A'})`);
  console.log(`📊 [HistoryManager] Stack size: Past: ${past.length + 1}, Future: 0`);

  return {
    past: present ? [...past, present] : past,
    present: newState,
    future: [] // Clear redo stack on new action
  };
};

// Deep equality check for state comparison
const isStateEqual = (state1: HistoryState, state2: HistoryState): boolean => {
  const safeStr = (obj: any) => JSON.stringify(obj || {});

  const checks = [
    { name: 'layers', equal: safeStr(state1.dynamicLayers) === safeStr(state2.dynamicLayers) },
    { name: 'positions', equal: safeStr(state1.layerPositions) === safeStr(state2.layerPositions) },
    { name: 'text', equal: safeStr(state1.textContents) === safeStr(state2.textContents) },
    { name: 'styles', equal: safeStr(state1.textStyles) === safeStr(state2.textStyles) },
    { name: 'shapes', equal: safeStr(state1.shapeSettings) === safeStr(state2.shapeSettings) },
    { name: 'bg', equal: safeStr(state1.backgroundSettings) === safeStr(state2.backgroundSettings) },
    { name: 'images', equal: safeStr(state1.imageSettings) === safeStr(state2.imageSettings) },
    { name: 'video', equal: safeStr(state1.videoSettings) === safeStr(state2.videoSettings) },
    { name: 'scroller', equal: safeStr(state1.scrollerSettings) === safeStr(state2.scrollerSettings) },
    { name: 'widget', equal: safeStr(state1.widgetSettings) === safeStr(state2.widgetSettings) },
    { name: 'templateSettings', equal: safeStr(state1.templateSettings) === safeStr(state2.templateSettings) },
    { name: 'canvas', equal: safeStr(state1.canvasDimensions) === safeStr(state2.canvasDimensions) },
    { name: 'pageIndex', equal: state1.currentPageIndex === state2.currentPageIndex },
    { name: 'pageName', equal: state1.pageName === state2.pageName },
    { name: 'pages', equal: safeStr(state1.pages) === safeStr(state2.pages) },
    { name: 'id', equal: state1.id === state2.id }
  ];

  const allEqual = checks.every(c => c.equal);

  if (!allEqual) {
    const diffs = checks.filter(c => !c.equal).map(c => c.name);
    console.log(`🔍 [HistoryManager] Diffs detected in: ${diffs.join(', ')}`);
  }

  return allEqual;
};

// Maximum history size to prevent memory issues
const MAX_HISTORY_SIZE = 50;

export const limitHistorySize = (history: HistoryManager): HistoryManager => {
  if (history.past.length <= MAX_HISTORY_SIZE) {
    return history;
  }

  return {
    ...history,
    past: history.past.slice(history.past.length - MAX_HISTORY_SIZE)
  };
};
